import {
  Button,
  FlexBox,
  FormField,
  FormGrid,
  Icon,
  IconNames,
  Page,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useMemo } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';

import {
  AllowedOffersWhileOnCall,
  presenceApi,
  PresenceOrganizationWorkloadSettings,
  WorkloadLimits,
} from 'apis/presence';
import { HookFormSwitch, HookFormText } from 'components/hookForm';
// eslint-disable-next-line no-restricted-imports
import { ToggleBox, ToggleBoxHead } from 'fable/toggleBox';
import { NotificationType, useNotifications } from 'hooks/useNotifications';

export const SettingsAgentsWorkloadsForm = (): JSX.Element => {
  const notify = useNotifications();

  const { workloadSettings } =
    presenceApi.endpoints.readOrganizationWorkloadSettings.useQueryState(
      undefined,
      {
        selectFromResult: ({ data, ...rest }) => ({
          ...rest,
          workloadSettings: data?.data ?? {
            load_limits: {
              email: 1,
              chat: 1,
              voicemail: 1,
              messaging: 1,
            },
            allowed_offers_while_on_call: {
              email: true,
              chat: true,
              voicemail: true,
              messaging: true,
            },
          },
        }),
      },
    );

  const form = useForm<PresenceOrganizationWorkloadSettings>({
    mode: 'onBlur',
    defaultValues: workloadSettings,
  });

  const [updateOrganizationWorkloadSettings] =
    presenceApi.useUpdateOrganizationWorkloadSettingsMutation();

  const handleValidSubmit: SubmitHandler<
    PresenceOrganizationWorkloadSettings
  > = async (values) => {
    try {
      await updateOrganizationWorkloadSettings(values);

      notify(
        {
          title: `Workload settings updated`,
          text: `The workload settings of the agents have been updated.`,
          time: 3000,
        },
        NotificationType.Success,
      );

      ampli.settingsAgentsWorkloadsFormSubmitted({ payload: values });
    } catch {
      notify(
        {
          title:
            'An error occurred when trying to update the workload settings.',
        },
        NotificationType.Error,
      );
    }
  };

  const allowedOffersWhileOnCall = useWatch({
    control: form.control,
    name: 'allowed_offers_while_on_call',
  });

  const enableConversationOffers = useMemo(
    () =>
      Object.entries(allowedOffersWhileOnCall ?? {}).some(
        ([_key, value]) => value,
      ),
    [allowedOffersWhileOnCall],
  );

  const handleEnableConversationOffers = useCallback(
    (isEnabled: boolean) => {
      form.setValue('allowed_offers_while_on_call', {
        email: isEnabled,
        chat: isEnabled,
        voicemail: isEnabled,
        messaging: isEnabled,
      });
    },
    [form],
  );

  return (
    <form
      noValidate
      tabIndex={-1}
      onSubmit={form.handleSubmit(handleValidSubmit)}
    >
      <Page.Section
        title="Workload limits of agents"
        controls={
          <Button
            type="submit"
            isLoading={form.formState.isSubmitting}
            disabled={!form.formState.isValid || form.formState.isSubmitting}
          >
            Save
          </Button>
        }
      >
        <FormGrid>
          {Object.entries(channelsWithAssets).map(
            ([key, { label, tooltip }]) => (
              <FormField key={key} span={3}>
                <HookFormText
                  name={`load_limits.${key as keyof WorkloadLimits}`}
                  label={`${label} limit`}
                  control={form.control}
                  rules={{
                    required: 'Please fill out this field',
                    min: {
                      value: 1,
                      message: 'The minimum value is 1',
                    },
                    max: {
                      value: 30,
                      message: 'The maximum value is 30',
                    },
                  }}
                  componentProps={{
                    type: 'number',
                    min: 1,
                    max: 30,
                    step: 1,
                  }}
                  helpIcon={{
                    title: `${label} limit`,
                    text: tooltip,
                  }}
                />
              </FormField>
            ),
          )}

          <FormField span={3}>
            <ToggleBox
              title="Enable conversations offers when on phone"
              isOpen={enableConversationOffers}
              headComponent={ToggleBoxHead}
              textProps={{ variant: '3' }}
              input={{
                name: 'enableConversationOffers',
                value: '',
              }}
              onChange={handleEnableConversationOffers}
            >
              <FlexBox flexDirection="column" padding="300" gap="300">
                {Object.entries(channelsWithAssets).map(
                  ([key, { label, icon }]) => (
                    <FlexBox
                      key={key}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <FlexBox gap="200" alignItems="center" flex="1">
                        <Icon name={icon} color="primary" />
                        {label}
                      </FlexBox>

                      <HookFormSwitch
                        name={`allowed_offers_while_on_call.${
                          key as keyof AllowedOffersWhileOnCall
                        }`}
                        control={form.control}
                      />
                    </FlexBox>
                  ),
                )}
              </FlexBox>
            </ToggleBox>
          </FormField>
        </FormGrid>
      </Page.Section>
    </form>
  );
};

const channelsWithAssets: Record<
  string,
  { label: string; icon: IconNames; tooltip: string }
> = {
  email: {
    label: 'Email',
    icon: 'mail',
    tooltip:
      'The maximum number of simultaneous emails or contact forms that an agent will be offered at any given time.',
  },
  chat: {
    label: 'Chat',
    icon: 'chat',
    tooltip:
      'The maximum number of simultaneous Messenger and chats that an agent will be offered at any given time.',
  },
  voicemail: {
    label: 'Voicemail',
    icon: 'voicemail',
    tooltip:
      'The maximum number of simultaneous voicemails that an agent will be offered at any given time.',
  },
  messaging: {
    label: 'Messaging',
    icon: 'messaging-settings',
    tooltip:
      'The maximum number of simultaneous third-party messaging conversations that an agent will be offered at any given time.',
  },
};
